import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from './components/ui/Button';
import { Card } from './components/ui/Card';
import { useApi } from './lib/api';
import { Select } from './components/ui/Select';
import { typography } from './theme';
import { useNavigate } from 'react-router-dom';
import {
  describeRoleError,
  fetchAdminMe,
  fetchAdminUserRoles,
  setAdminRole,
} from './api/admin';
import type { AdminMeResponse, AdminRoleUserResponse } from '@shared/schemas/admin-access';
import {
  STOP_IMPERSONATING,
  describeImpersonationError,
  useSubmitImpersonationMapping,
} from './hooks/useImpersonation';

export default function AdminMapping() {
  const { apiFetch } = useApi();
  const submitMapping = useSubmitImpersonationMapping();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const adminQuery = useQuery<AdminMeResponse>({
    queryKey: ['admin', 'me'],
    queryFn: () => fetchAdminMe(apiFetch),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const adminError = adminQuery.error;
  const adminData = adminQuery.data;

  const [selectedOwnerEmail, setSelectedOwnerEmail] = useState('');
  const [selectedRoleEmail, setSelectedRoleEmail] = useState('');
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [roleMessage, setRoleMessage] = useState<string | null>(null);
  const [roleErrorMessage, setRoleErrorMessage] = useState<string | null>(null);
  const [defaultsHydrated, setDefaultsHydrated] = useState(false);

  useEffect(() => {
    if (!adminData || defaultsHydrated) return;
    const normalizedEmail = adminData.ownerEmail?.toLowerCase() ?? '';
    const actingEmail = adminData.actingUser?.ownerEmail?.toLowerCase() ?? '';
    setSelectedOwnerEmail(actingEmail);
    const ownerEmails = adminData.ownerOptions.map((opt) => opt.ownerEmail);
    const defaultRoleEmail = ownerEmails.includes(actingEmail)
      ? actingEmail
      : ownerEmails.includes(normalizedEmail)
        ? normalizedEmail
        : '';
    setSelectedRoleEmail(defaultRoleEmail);
    setDefaultsHydrated(true);
  }, [adminData, defaultsHydrated]);

  const normalizedRoleEmail = selectedRoleEmail.trim().toLowerCase();
  // Only show role management section for full admins (not impersonators)
  const isFullAdmin = (adminData?.roles || []).some((role) => role.slug === 'admin');

  const roleQuery = useQuery<AdminRoleUserResponse>({
    queryKey: ['admin', 'roles', normalizedRoleEmail],
    queryFn: () => fetchAdminUserRoles(apiFetch, normalizedRoleEmail),
    enabled: isFullAdmin && normalizedRoleEmail.length > 0,
    staleTime: 0,
  });

  useEffect(() => {
    if (roleQuery.error) {
      setRoleErrorMessage(describeRoleError(roleQuery.error, 'load'));
    } else {
      setRoleErrorMessage(null);
    }
  }, [roleQuery.error]);

  const mappingMutation = useMutation({
    mutationFn: (ownerEmail: string) => submitMapping(
      ownerEmail === '' ? STOP_IMPERSONATING : { action: 'start', target: { email: ownerEmail } },
    ),
    onSuccess: () => {
      // Full page navigation resets all client state (React Query caches,
      // SSE streams, user-controls). The delegation is already created
      // server-side — no need to set ?impersonate= in the URL.
      window.location.href = '/';
    },
    onError: (error: unknown) => {
      setSavedMessage(describeImpersonationError(error));
    }
  });

  const updateRoleMutation = useMutation({
    mutationFn: async (payload: { email: string; roleSlug: string; nextChecked: boolean }) => {
      const asyncResponse = await setAdminRole(apiFetch, {
        email: payload.email,
        roleSlug: payload.roleSlug,
        grant: payload.nextChecked,
      });
      return { asyncResponse, request: payload };
    },
    onSuccess: ({ asyncResponse, request }) => {
      const user = asyncResponse.user;
      if (user) {
        queryClient.setQueryData(['admin', 'roles', request.email], user);
      }
      const roleLabel = adminData?.roleOptions.find((opt) => opt.slug === request.roleSlug)?.name
        || request.roleSlug.replace(/_/g, ' ');
      setRoleMessage(`${roleLabel} ${request.nextChecked ? 'granted' : 'revoked'}`);
      // Bound outside the updater: narrowing does not survive into the closure.
      const context = asyncResponse.context;
      if (context) {
        queryClient.setQueryData<AdminMeResponse | undefined>(['admin', 'me'], (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            roles: context.authUser.roles,
            actingUser: context.actingUser,
            delegation: context.delegation || null,
          };
        });
      }
      // Grant/revoke changes the acting admin's own roles (self-grant is
      // possible for full admins). /api/me is keyed ['/api/me', principalKey]
      // and cached with staleTime: Infinity (UserControlsContext), so without
      // this the acting admin's RequireRole guards and AdminTabBar stay on
      // stale roles until a hard refresh (R10).
      void queryClient.invalidateQueries({ queryKey: ['/api/me'] });
      setRoleErrorMessage(null);
    },
    onError: (error: unknown) => {
      setRoleErrorMessage(describeRoleError(error, 'update'));
    }
  });

  useEffect(() => {
    setRoleMessage(null);
    setRoleErrorMessage(null);
  }, [normalizedRoleEmail]);

  const ownerOptions = useMemo(() => adminData?.ownerOptions ?? [], [adminData?.ownerOptions]);
  const roleOptions = adminData?.roleOptions ?? [];
  const ownerEmails = useMemo(() => ownerOptions.map((opt) => opt.ownerEmail), [ownerOptions]);
  const roleUser = roleQuery.data;
  const assignedRoleSlugs = useMemo(() => new Set((roleUser?.roles || []).map((role) => role.slug)), [roleUser]);
  const actingSlackId = adminData?.actingUser?.slackUserId || adminData?.slackUserId || '—';
  const actingOwner = adminData?.actingUser?.ownerEmail || '—';
  const roleList = (adminData?.roles || []).map((role) => role.slug.replace(/_/g, ' ')).join(', ');
  const actingRoleList = (adminData?.actingUser?.roles || []).map((role) => role.slug.replace(/_/g, ' ')).join(', ');
  const currentActingEmail = adminData?.actingUser?.ownerEmail?.toLowerCase() || '';
  const normalizedSelectedOwner = selectedOwnerEmail.trim().toLowerCase();
  const canSave = defaultsHydrated && normalizedSelectedOwner !== currentActingEmail && !mappingMutation.isPending;
  const roleLoading = roleQuery.isLoading || roleQuery.isFetching;
  const roleSaving = updateRoleMutation.isPending;

  const onOwnerSelect = (value: string) => {
    setSelectedOwnerEmail(value);
    setSavedMessage(null);
  };

  const handleRoleSelect = (value: string) => {
    setSelectedRoleEmail(value);
  };

  const handleRoleToggle = (roleSlug: string, nextChecked: boolean) => {
    if (!normalizedRoleEmail) {
      setRoleErrorMessage('Cannot update roles without an owner email.');
      return;
    }
    setRoleMessage(null);
    updateRoleMutation.mutate({ email: normalizedRoleEmail, roleSlug, nextChecked });
  };

  const handleSave = () => {
    setSavedMessage(null);
    mappingMutation.mutate(selectedOwnerEmail);
  };

  if (adminError) {
    return <div className="p-6 text-red-600">Error: {adminError.message}</div>;
  }

  if (!adminData) {
    return (
      <div className="max-w-3xl mx-auto p-4 font-sans">
        <Card>
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-40 rounded bg-slate-200" />
            <div className="h-20 rounded bg-slate-100" />
            <div className="h-10 rounded bg-slate-100" />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 font-sans">
      <div className="mb-2 flex items-center justify-between">
        <h2 className={typography.headingMd}>Delegate Your Access</h2>
        <button
          type="button"
          className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
          onClick={() => navigate('/')}
        >
          ← Back to App Home
        </button>
      </div>
      <p className="mb-4 text-sm text-slate-500">
        Choose who you should act as in App Home. Your current mapping is locked for reference—pick a new owner email to update it.
      </p>
      <Card>
        <div className="grid gap-4">
          <div className="grid gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">You currently act as</span>
            <div className="flex flex-col gap-1 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Slack user</span>
                <span className="font-mono text-sm text-slate-600">{actingSlackId}</span>
              </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Owner email</span>
              <span className="font-mono text-sm text-slate-600">{actingOwner}</span>
            </div>
            {actingRoleList && (
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Acting roles</span>
                <span className="font-mono text-sm text-slate-600">{actingRoleList}</span>
              </div>
            )}
            {adminData?.delegation && (
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Delegation</span>
                <span className="font-mono text-sm text-slate-600">{adminData.delegation.role.name}{adminData.delegation.expiresAt ? ` (expires ${new Date(adminData.delegation.expiresAt).toLocaleString()})` : ''}</span>
              </div>
            )}
          </div>
        </div>
        {roleList && (
          <div className="grid gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Your roles</span>
            <div className="rounded-md border border-slate-200 bg-white p-3 text-xs text-slate-600">{roleList}</div>
          </div>
        )}
          {ownerEmails.length > 0 ? (
            <div>
              <label className="text-sm font-medium text-slate-600" htmlFor="new-owner-email">Act as another teammate</label>
              <Select id="new-owner-email" value={selectedOwnerEmail} onChange={(e) => onOwnerSelect((e.target as HTMLSelectElement).value)}>
                <option value="">Stop acting as anyone</option>
                {ownerEmails.map((ownerEmail) => (
                  <option key={ownerEmail} value={ownerEmail}>{ownerEmail}</option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-slate-500">Choose someone to act on behalf of, or select “Stop acting as anyone” to revert to your own account.</p>
            </div>
          ) : (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              No owner emails available. Ask an admin to add one before updating your mapping.
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button variant="primary" onClick={handleSave} disabled={!canSave}>Save</Button>
            {savedMessage && <span className="text-sm text-gray-600">{savedMessage}</span>}
          </div>
        </div>
      </Card>

      {/* Manage Roles section - only visible to full admins */}
      {isFullAdmin && (
        <Card className="mt-6">
          <div className="grid gap-4">
            <div className="flex items-center justify-between">
              <h3 className={`${typography.headingMd}`}>Manage Roles</h3>
            </div>
            <p className="text-sm text-slate-500">
              Check or uncheck roles to grant or revoke them for a teammate. Changes apply immediately.
            </p>
            {ownerEmails.length > 0 ? (
              <div>
                <label className="text-sm font-medium text-slate-600" htmlFor="manage-role-email">Select a teammate</label>
                <Select
                  id="manage-role-email"
                  value={selectedRoleEmail}
                  onChange={(event) => handleRoleSelect((event.target as HTMLSelectElement).value)}
                  disabled={roleLoading}
                >
                  <option value="">Choose a teammate…</option>
                  {ownerEmails.map((ownerEmail) => (
                    <option key={ownerEmail} value={ownerEmail}>{ownerEmail}</option>
                  ))}
                </Select>
                <p className="mt-1 text-xs text-slate-500">Pick a teammate to view and adjust their roles.</p>
              </div>
            ) : (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                No owner emails available. Ask an admin to add one before managing roles.
              </div>
            )}

            {roleErrorMessage && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{roleErrorMessage}</div>
            )}
            {roleMessage && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{roleMessage}</div>
            )}

            {roleLoading && !roleSaving && (
              <div className="text-sm text-slate-500">Loading roles…</div>
            )}

            {roleUser && !roleLoading && (
              <div className="rounded-md border border-slate-200 bg-white">
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  Managing roles for <span className="font-semibold">{roleUser.ownerEmail || roleUser.slackUserId}</span>
                </div>
                <div className="divide-y divide-slate-200">
                  {roleOptions.length === 0 && (
                    <div className="px-4 py-3 text-sm text-slate-500">No roles are available yet.</div>
                  )}
                  {roleOptions.map((role) => {
                    const assigned = assignedRoleSlugs.has(role.slug);
                    return (
                      <label key={role.slug} className="flex cursor-pointer items-start gap-3 px-4 py-3">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4"
                          checked={assigned}
                          disabled={roleSaving || roleLoading}
                          onChange={(event) => handleRoleToggle(role.slug, event.currentTarget.checked)}
                        />
                        <span className="flex flex-col">
                          <span className="text-sm font-medium text-slate-700">{role.name}</span>
                          {role.description && <span className="text-xs text-slate-500">{role.description}</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

    </div>
  );
}
